import { useMemo } from 'react'

import type { api } from './api'
import { useSession } from './SessionContext'

/* ============================================================================
   คิวอนุมัติของ HR — ใบลา / ใบปรับปรุงเวลา
   backend: src/routes/adminApprovals.js · src/lib/approvals.js

   ⚠️ หน่วยของ "1 ใบ" คือ **rowId** (HR_LEAVE.ROW_ID) ไม่ใช่ recId —
      ใบลาหลายวันมีหลายแถว (recId) แต่กดอนุมัติทีเดียวทั้งใบ
      ส่วนการ **แก้** ทำทีละแถว เพราะแต่ละวันมีเวลาของตัวเอง

   ⚠️ เวลาที่นี่เป็น **เวลาไทย** ทั้งหมด (ยกเว้น audit.at ที่เป็น UTC ของตารางเรา) —
      ห้ามเอา dateStart/dateEnd ไป new Date() แล้วแสดง จะเลื่อนไป +7 ชม.
   ============================================================================ */

export const APPROVALS_BASE = '/api/admin/approvals'

export type Paged<T> = { total: number; page: number; pageSize: number } & T

/** ประเภทคิว = แท็บบนหน้าจอ */
export type Kind = 'leave' | 'adjust'

export const KIND_LABEL: Record<Kind, string> = {
  leave: 'ใบลา',
  adjust: 'ใบปรับปรุงเวลา',
}

/** สถานะของใบ — ชื่อ/สีมาจาก backend (lib/approvals.js → STAGE) ไม่ต้อง map ซ้ำที่นี่ */
export type Stage = 'wait_hr' | 'wait_chain' | 'draft' | 'done' | 'reject' | 'cancel' | 'other'

export type Round = {
  roundId: string
  /** วันแรกของรอบ (ปกติวันที่ 21) */
  from: string
  /** วันสุดท้ายของรอบ **รวมวันนี้ด้วย** (ปกติวันที่ 20 ของเดือนถัดไป) */
  to: string
  toExclusive: string
  /** ปิดรอบแล้ว = ตัดเข้าเงินเดือนไปแล้ว การแก้ย้อนหลังต้องยืนยันซ้ำ */
  closed: boolean
  isCurrent: boolean
}

const ROUND_MONTH_SHORT = [
  'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.',
]

/**
 * '2026/09' + ช่วง → 'ก.ย. 2026 (21/09–20/10)'
 *
 * อยู่ที่นี่เพราะทั้ง PeriodFilter (หน้ารายงาน/อนุมัติของ admin) และหน้า `/me/*`
 * ต้องเขียนป้ายรอบให้เหมือนกัน — ถ้าก๊อปไปคนละที่ วันหนึ่งสองหน้าจะอ่านรอบ
 * เดียวกันไม่เหมือนกันแล้วผู้ใช้สับสนว่าดูคนละรอบอยู่
 */
export function roundLabel(r: Round): string {
  const m = Number(r.roundId.slice(5, 7))
  const name = m >= 1 && m <= 12 ? ROUND_MONTH_SHORT[m - 1] : r.roundId.slice(5)
  const dm = (d: string) => `${d.slice(8)}/${d.slice(5, 7)}`
  return `${name} ${r.roundId.slice(0, 4)} (${dm(r.from)}–${dm(r.to)})${r.closed ? ' · ปิดรอบ' : ''}`
}

export type ChainStep = {
  by: string | null
  name: string | null
  at?: string | null
  done: boolean
}

export type Form = {
  rowId: string
  empCode: string
  empName: string | null
  companyCode: string | null
  leaveType: string
  typeDesc: string
  /** ใบเดียวมีหลายประเภทปนกัน = ข้อมูลผิดปกติ ต้องเตือนไม่ใช่โชว์ประเภทแรกเฉย ๆ */
  mixedTypes?: boolean
  dayRows: number
  hours: number
  firstDay: string
  lastDay: string
  status: string
  statusText: string
  stage: Stage
  stageText: string
  stageTone: string
  /** กดอนุมัติ/ไม่อนุมัติได้ไหม — **backend เป็นคนตัดสิน** ห้ามคิดเองจาก stage */
  actionable: boolean
  editable: boolean
  waitingAt: { level: string; by: string; name: string | null } | null
  chain: { approve1: ChainStep; approve2: ChainStep; hr: ChainStep & { status: string | null } }
  reason: string | null
  createdAt: string | null
}

/** ใบเดียวแบบละเอียด — มีของที่คิว list ไม่ส่งมา */
export type FormDetail = Form & {
  isAdjust: boolean
  /** หัวหน้าอนุมัติไปแล้ว: แก้ได้แต่ข้อมูลจะต่างจากที่หัวหน้าเห็นตอนกด */
  chainAlreadyApproved: boolean
  totalHours: number
  /** มีไฟล์แนบหลักฐานไหม — เปิดดูผ่าน GET /forms/:rowId/evidence */
  hasEvidence?: boolean
}

export type FormRow = {
  recId: number
  leaveType: string
  typeDesc: string
  /** วันอ้างอิงของแถว (LEAVE_DATE) — ใบปรับปรุงเวลาที่ไม่มี dateStart ใช้ตัวนี้ */
  workDate: string | null
  dateStart: string | null
  dateEnd: string | null
  hours: number | null
  reason: string | null
}

export type AuditEntry = {
  /** UTC (ลงท้าย Z) — ต่างจากเวลาอื่นในไฟล์นี้ */
  at: string
  action: 'approve' | 'reject' | 'edit'
  actorUser: string
  actorEmp: string | null
  note: string | null
  affected: number
}

export type FormBundle = { form: FormDetail; rows: FormRow[]; audit: AuditEntry[] }

export type StageCount = { stage: Stage; text: string; tone: string; forms: number }

export type Queue = Paged<{
  kind: Kind
  round: Round
  forms: Form[]
  sort: { key: string; dir: 'asc' | 'desc' }[]
  counts: StageCount[]
}>

/** ชั่วโมงที่ระบบคิดให้จากกะจริง — เป็นค่าที่แนะนำ HR แก้ทับได้ */
export type HoursHint = {
  hours: number
  workDays: number
  holidayDays: number
  /** 'shift' คิดจากกะได้ · 'no-shift' ไม่มีข้อมูลกะ ต้องใส่เอง · 'span' ใบปรับปรุงเวลา */
  basis: 'shift' | 'no-shift' | 'span' | 'incomplete'
  countsHoliday?: boolean
}

export type Edit = {
  recId: number
  dateStart?: string | null
  dateEnd?: string | null
  leaveType?: string
  reason?: string
  /** ไม่ส่ง = ให้ backend คิดจากกะให้ · ส่ง = ใช้ค่านี้ */
  hours?: number
  remove?: true
}

const query = (params: Record<string, string | undefined>) => {
  const q = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') q.set(k, v)
  const s = q.toString()
  return s ? `?${s}` : ''
}

const num = (n: number | undefined) => (n === undefined ? undefined : String(n))

export type LeaveTypeOption = { leaveType: string; desc: string; isAdjust: boolean }

export function approvalsApi(call: typeof api) {
  return {
    rounds: () => call<{ rounds: Round[]; current: string | null }>(`${APPROVALS_BASE}/rounds`),

    leaveTypes: () =>
      call<{ types: LeaveTypeOption[]; holidayCounted: string[] }>(`${APPROVALS_BASE}/leave-types`),

    queue: (opts: {
      kind: Kind
      round?: string
      stage?: Stage | ''
      q?: string
      page?: number
      pageSize?: number
      sort?: string
    }) =>
      call<Queue>(
        `${APPROVALS_BASE}/queue${query({
          kind: opts.kind,
          round: opts.round,
          stage: opts.stage || undefined,
          q: opts.q,
          page: num(opts.page),
          pageSize: num(opts.pageSize),
          sort: opts.sort,
        })}`,
      ),

    form: (rowId: string) => call<FormBundle>(`${APPROVALS_BASE}/forms/${encodeURIComponent(rowId)}`),

    hours: (rowId: string, at: { leaveType: string; dateStart: string | null; dateEnd: string | null }) =>
      call<HoursHint>(
        `${APPROVALS_BASE}/forms/${encodeURIComponent(rowId)}/hours${query({
          leaveType: at.leaveType,
          dateStart: at.dateStart ?? undefined,
          dateEnd: at.dateEnd ?? undefined,
        })}`,
      ),

    act: (rowId: string, action: 'APPROVE' | 'REJECT', note?: string) =>
      call<{ ok: true; affected: number; rows: number }>(
        `${APPROVALS_BASE}/forms/${encodeURIComponent(rowId)}/act`,
        { method: 'POST', body: JSON.stringify({ action, note }) },
      ),

    /** อนุมัติ/ไม่อนุมัติ "ชั้นหัวหน้า" — backend เช็คว่าผู้กดเป็นหัวหน้าชั้นที่ค้างจริง */
    actChain: (rowId: string, action: 'APPROVE' | 'REJECT', note?: string) =>
      call<{ ok: true; affected: number; level: string }>(
        `${APPROVALS_BASE}/forms/${encodeURIComponent(rowId)}/act-chain`,
        { method: 'POST', body: JSON.stringify({ action, note }) },
      ),

    /** ยกเลิกใบ (HR/admin) — รวมใบที่อนุมัติแล้ว (ล้าง HR_EMP_LEAVE ให้คิดเวลาใหม่) */
    cancel: (rowId: string, reason?: string) =>
      call<{ ok: true; affected: number; wasClosed: boolean }>(
        `${APPROVALS_BASE}/forms/${encodeURIComponent(rowId)}/cancel`,
        { method: 'POST', body: JSON.stringify({ reason }) },
      ),

    edit: (rowId: string, body: { edits: Edit[]; note: string; confirmClosedRound?: boolean }) =>
      call<{ ok: true; affected: number; requested: number; form: FormBundle }>(
        `${APPROVALS_BASE}/forms/${encodeURIComponent(rowId)}`,
        { method: 'PATCH', body: JSON.stringify(body) },
      ),
  }
}

export function useApprovals() {
  const { call } = useSession()
  return useMemo(() => approvalsApi(call), [call])
}
